import URLS from '@/store/urls';
import apiCall from '@/store/apiCall';
import notificationWithIcon from '@/utils/notification';
import { showErrorNotification } from '@/utils/errorHandler';

const actions = {
  async getCompanyPages({ commit, state }, companyId) {
    // Check cache - if already loaded for this company, skip fetch
    if (state.lastLoadedCompanyId === companyId) {
      return state.companyPages;
    }

    commit('GET_COMPANY_PAGES_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPage}/company/${companyId}`,
        isToken: true,
      });
      commit('GET_COMPANY_PAGES_SUCCESS', { data: data.data, total: data.total, companyId });
      return data;
    } catch (err) {
      commit('GET_COMPANY_PAGES_ERROR');
      showErrorNotification(err);
      throw err;
    }
  },

  async forceRefreshCompanyPages({ commit }, companyId) {
    commit('CLEAR_COMPANY_PAGES_CACHE');
    commit('GET_COMPANY_PAGES_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPage}/company/${companyId}`,
        isToken: true,
      });
      commit('GET_COMPANY_PAGES_SUCCESS', { data: data.data, total: data.total, companyId });
      return data;
    } catch (err) {
      commit('GET_COMPANY_PAGES_ERROR');
      showErrorNotification(err);
      throw err;
    }
  },

  async getCompanyPage({ commit }, id) {
    commit('GET_COMPANY_PAGE_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPage}/${id}`,
        isToken: true,
      });
      commit('GET_COMPANY_PAGE_SUCCESS', data.data);
      return data.data;
    } catch (err) {
      commit('GET_COMPANY_PAGE_ERROR');
      showErrorNotification(err);
      throw err;
    }
  },

  async createCompanyPage({ dispatch, state }, { companyId, pageData }) {
    try {
      const footerOrder = (state.companyPages?.length || 0) + 1;
      const payload = {
        companyId,
        url: pageData.url,
        seoTitle: pageData.seoTitle || '',
        seoDescription: pageData.seoDescription || '',
        seoKeywords: pageData.seoKeywords || '',
        name: pageData.name,
        showInNav: pageData.showInNav || false,
        footerOrder,
        isPublished: false,
        publishedAt: null,
      };

      await apiCall({
        type: 'POST',
        url: URLS.companyPage,
        body: payload,
        isToken: true,
      });
      notificationWithIcon('success', 'Page created successfully');
      dispatch('forceRefreshCompanyPages', companyId);
      return true;
    } catch (err) {
      showErrorNotification(err);
      return false;
    }
  },

  async updateCompanyPage({ dispatch }, { id, companyId, pageData }) {
    try {
      const payload = {
        url: pageData.url,
        seoTitle: pageData.seoTitle || '',
        seoDescription: pageData.seoDescription || '',
        seoKeywords: pageData.seoKeywords || '',
        name: pageData.name,
        showInNav: pageData.showInNav || false,
        footerOrder: pageData.footerOrder,
        isPublished: pageData.isPublished || false,
        publishedAt: pageData.publishedAt || null,
      };

      await apiCall({
        type: 'PUT',
        url: `${URLS.companyPage}/${id}`,
        body: payload,
        isToken: true,
      });
      notificationWithIcon('success', 'Page updated successfully');
      dispatch('forceRefreshCompanyPages', companyId);
      return true;
    } catch (err) {
      showErrorNotification(err);
      return false;
    }
  },

  async deleteCompanyPage({ dispatch }, { id, companyId }) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.companyPage}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Page deleted successfully');
      dispatch('forceRefreshCompanyPages', companyId);
      return true;
    } catch (err) {
      showErrorNotification(err);
      return false;
    }
  },

  async duplicateCompanyPage({ dispatch }, { pageId, companyId }) {
    try {
      await apiCall({
        type: 'POST',
        url: `${URLS.companyPage}/${pageId}/duplicate`,
        isToken: true,
      });
      notificationWithIcon('success', 'Page duplicated successfully');
      dispatch('forceRefreshCompanyPages', companyId);
      return true;
    } catch (err) {
      showErrorNotification(err);
      return false;
    }
  },

  // ── Draft actions ──

  async getDraft(_, pageId) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPage}/${pageId}/draft`,
        isToken: true,
      });
      return data.data || null;
    } catch (err) {
      // 404 means no draft exists — not an error
      if (err?.response?.status === 404) return null;
      showErrorNotification(err);
      return null;
    }
  },

  async createDraft({ dispatch }, { pageId, companyId }) {
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.companyPage}/${pageId}/draft`,
        isToken: true,
      });
      notificationWithIcon('success', 'Draft created');
      dispatch('forceRefreshCompanyPages', companyId);
      return data.data;
    } catch (err) {
      showErrorNotification(err);
      return null;
    }
  },

  async publishDraft({ dispatch }, { draftId, companyId }) {
    try {
      const { data } = await apiCall({
        type: 'PUT',
        url: `${URLS.companyPage}/draft/${draftId}/publish`,
        isToken: true,
      });
      notificationWithIcon('success', 'Draft published successfully');
      dispatch('forceRefreshCompanyPages', companyId);
      return data.data;
    } catch (err) {
      showErrorNotification(err);
      return null;
    }
  },

  async discardDraft({ dispatch }, { draftId, companyId }) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.companyPage}/draft/${draftId}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Draft discarded');
      dispatch('forceRefreshCompanyPages', companyId);
      return true;
    } catch (err) {
      showErrorNotification(err);
      return false;
    }
  },
};

export default actions;
